import { createAsyncThunk } from '@reduxjs/toolkit';
import { sendGetRequest, sendPostRequest } from '../../../api/requestUtil';

export const MY_READS_PAGE_SIZE = 20;

export const fetchMyReads = createAsyncThunk<
  {
    bookmarkedBlogCount: number;
    unreadCount: number;
    posts: MyReadsPostType[];
    pagination: BlogPostsPaginationType;
  },
  { page?: number; isRead?: boolean } | void,
  {
    state: AppState;
    rejectValue: string;
  }
>(
  'myReads/fetchMyReads',
  async (arg, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const isLoggedIn = state.user.isLoggedIn;
    const token = state.user.authToken;

    if (!token || !isLoggedIn) {
      return rejectWithValue('Not authenticated. Please log in.');
    }

    const page = arg?.page || 1;
    const isReadParam =
      arg?.isRead === undefined ? '' : `&is_read=${arg.isRead}`;

    try {
      const response = await sendGetRequest(
        `/api/blogs/my-reads/?page=${page}&page_size=${MY_READS_PAGE_SIZE}${isReadParam}`,
        token
      );
      return {
        bookmarkedBlogCount: response.bookmarked_blog_count,
        unreadCount: response.unread_count,
        posts: response.posts,
        pagination: response.pagination,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'An unknown error occurred while fetching your reads';
      console.error('Error fetching my reads:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

// Used only by the sidebar badge poller. It must NOT touch `posts`/`pagination` -
// those belong to whichever tab/page MyReadsPage last fetched, and this runs
// on its own interval independently of that, so writing to them there causes
// a race where this response can land after the page's and clobber its list
// with the wrong (unfiltered) set of posts.
export const fetchMyReadsUnreadCount = createAsyncThunk<
  number,
  void,
  {
    state: AppState;
    rejectValue: string;
  }
>(
  'myReads/fetchMyReadsUnreadCount',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const isLoggedIn = state.user.isLoggedIn;
    const token = state.user.authToken;

    if (!token || !isLoggedIn) {
      return rejectWithValue('Not authenticated. Please log in.');
    }

    try {
      // page_size=1: only `unread_count` (computed over the whole set) is used.
      const response = await sendGetRequest(
        `/api/blogs/my-reads/?page=1&page_size=1`,
        token
      );
      return response.unread_count;
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.detail ||
        error?.message ||
        'An unknown error occurred while fetching unread count';
      console.error('Error fetching my reads unread count:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const markAllRead = createAsyncThunk<
  void,
  void,
  {
    state: AppState;
    rejectValue: string;
  }
>(
  'myReads/markAllRead',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    if (!token) {
      return rejectWithValue('Authentication token missing.');
    }
    try {
      await sendPostRequest(`/api/blogs/my-reads/mark-all-read/`, {}, token);
    } catch (error: any) {
      const errorMessage =
        error?.detail || error?.message || 'Failed to mark all as read.';
      console.error('Error marking all posts as read:', error);
      return rejectWithValue(errorMessage);
    }
  }
);

export const markPostRead = createAsyncThunk<
  { postId: number; isRead: boolean },
  { postId: number; isRead: boolean },
  {
    state: AppState;
    rejectValue: string;
  }
>(
  'myReads/markPostRead',
  async ({ postId, isRead }, { getState, rejectWithValue }) => {
    const state = getState() as AppState;
    const token = state.user.authToken;
    if (!token) {
      return rejectWithValue('Authentication token missing.');
    }
    try {
      await sendPostRequest(
        `/api/blogs/my-reads/mark/`,
        { post_id: postId, is_read: isRead },
        token
      );
      return { postId, isRead };
    } catch (error: any) {
      const errorMessage =
        error?.detail || error?.message || 'Failed to update read status.';
      console.error('Error marking post read/unread:', error);
      return rejectWithValue(errorMessage);
    }
  }
);
